"use client"

import React, { useState, useEffect } from "react"
import dayjs from "dayjs"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer, ChartLegend, ChartLegendContent, type ChartConfig } from "@/components/ui/chart"
import { formatCurrency } from "@/lib/utils"

interface SalaryProgressionPoint {
  year: number
  oldPay: number
  newPay: number
  difference: number
}

interface DetailedProgressionPoint {
  date: string
  oldPay: number
  newPay: number
  difference: number
  monthsOfService: number
  isServiceMilestone: boolean
  isRateIncrease: boolean
}

interface SalaryProgressionChartProps {
  data: SalaryProgressionPoint[]
  detailedData: DetailedProgressionPoint[]
}

type ChartCopyData = {
  title: string
  descriptions: {
    monthly: string
    annual: string
  }
  toggleLabels: {
    annual: string
    monthly: string
    toggleAriaLabel: string
  }
  legend: {
    current: string
    new: string
  }
  tooltip: {
    current: string
    new: string
    difference: string
  }
}

const defaultChartCopy: ChartCopyData = {
  title: "Salary Progression Projection",
  descriptions: {
    monthly: "Monthly pay comparison histogram showing exact service milestones and rate increases",
    annual: "Calendar year earnings comparison histogram showing rate progression and annual increases"
  },
  toggleLabels: {
    annual: "Annual",
    monthly: "Monthly",
    toggleAriaLabel: "Toggle between annual and monthly view"
  },
  legend: {
    current: "Current",
    new: "New"
  },
  tooltip: {
    current: "Current:",
    new: "New:",
    difference: "Difference:"
  }
}

export function SalaryProgressionChart({ data, detailedData }: SalaryProgressionChartProps) {
  const [showMonthly, setShowMonthly] = useState(false)
  const [chartCopy, setChartCopy] = useState<ChartCopyData>(defaultChartCopy)
  const [copyLoading, setCopyLoading] = useState(true)

  // Load copy data
  useEffect(() => {
    const loadCopy = async () => {
      try {
        const response = await fetch('/copy.json')
        if (response.ok) {
          const copyData = await response.json()
          if (copyData.chart) {
            setChartCopy(copyData.chart)
          }
        }
      } catch (error) {
        console.warn('Failed to load copy.json for chart, using defaults')
      } finally {
        setCopyLoading(false)
      }
    }
    
    loadCopy()
  }, [])

  const chartConfig = {
    oldPay: {
      label: chartCopy.legend.current,
      color: "#9ca3af",
    },
    newPay: {
      label: chartCopy.legend.new,
      color: "#2563eb",
    },
  } satisfies ChartConfig
  
  // Format data for the annual chart - ensure numeric values
  const chartData = data.map(point => ({
    year: point.year.toString(),
    oldPay: Number(Math.round(point.oldPay)),
    newPay: Number(Math.round(point.newPay)),
    difference: Number(Math.round(point.difference))
  }))

  // Format data for the detailed monthly chart
  const detailedChartData = detailedData.map(point => ({
    date: point.date,
    displayDate: dayjs(point.date, 'YYYY-MM').format('MMM YYYY'), // Clean dayjs parsing and formatting
    oldPay: Number(Math.round(point.oldPay)),
    newPay: Number(Math.round(point.newPay)),
    difference: Number(Math.round(point.difference)),
    monthsOfService: point.monthsOfService,
    isServiceMilestone: point.isServiceMilestone,
    isRateIncrease: point.isRateIncrease
  }))

  const currentData = showMonthly ? detailedChartData : chartData

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean
    payload?: Array<{
      color: string
      dataKey: string
      value: number
      payload?: {
        oldPay: number
        newPay: number
        difference: number
        year?: string
        date?: string
      }
    }>
    label?: string
  }) => {
    if (active && payload && payload.length > 0) {
      const dataPoint = payload[0].payload
      
      if (!dataPoint) return null
      
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-4 min-w-[275px]">
          <p className="font-semibold text-card-foreground mb-3 text-center">{label}</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">{chartCopy.tooltip.current}</span>
              <span className="text-muted-foreground">{formatCurrency(dataPoint.oldPay)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-primary">{chartCopy.tooltip.new}</span>
              <span className="text-primary">{formatCurrency(dataPoint.newPay)}</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium">{chartCopy.tooltip.difference}</span>
                <span className={`font-bold ${dataPoint.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(() => {
                    const amount = formatCurrency(dataPoint.difference);
                    const percentage = dataPoint.oldPay > 0 ? ((dataPoint.difference / dataPoint.oldPay) * 100).toFixed(1) : '0.0';
                    
                    if (dataPoint.difference > 0) {
                      return '+' + amount + ' (+' + percentage + '%)';
                    } else if (dataPoint.difference < 0) {
                      return amount + ' (' + percentage + '%)';
                    } else {
                      return amount + ' (0.0%)';
                    }
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  if (copyLoading) {
    return (
      <Card className="w-full shadow-lg">
        <CardContent className="p-6 flex items-center justify-center min-h-[300px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-3"></div>
            <p className="text-sm text-muted-foreground">Loading chart...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full shadow-lg">
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div>
            <CardTitle className="text-xl font-semibold text-foreground">
              {chartCopy.title}
            </CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              {showMonthly 
                ? chartCopy.descriptions.monthly
                : chartCopy.descriptions.annual
              }
            </p>
          </div>
          <div className="flex items-center gap-3 sm:ml-4 mt-1 sm:mt-0 self-center sm:self-auto justify-center sm:justify-start">
            <span className={`text-sm font-medium transition-colors ${!showMonthly ? 'text-primary' : 'text-muted-foreground'}`}>
              {chartCopy.toggleLabels.annual}
            </span>
            <button
              type="button"
              onClick={() => setShowMonthly(!showMonthly)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                showMonthly ? 'bg-primary' : 'bg-muted'
              }`}
              role="switch"
              aria-checked={showMonthly}
              aria-label={chartCopy.toggleLabels.toggleAriaLabel}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showMonthly ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium transition-colors ${showMonthly ? 'text-primary' : 'text-muted-foreground'}`}>
              {chartCopy.toggleLabels.monthly}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-100 w-full">
          <BarChart
            data={currentData}
            margin={{
              top: 40,
              right: 50,
              left: 20,
              bottom: 25,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
            <XAxis 
              dataKey={showMonthly ? "displayDate" : "year"}
              tickLine={false}
              axisLine={false}
              className="text-sm"
              tickMargin={25}
              minTickGap={showMonthly ? 10 : 8}
              interval={showMonthly ? "preserveStartEnd" : 0}
            />
            <YAxis 
              tickFormatter={(value) => {
                const thousands = (value / 1000).toFixed(0);
                return '$' + thousands + 'k';
              }}
              tickLine={false}
              axisLine={false}
              className="text-sm"
              tickMargin={25}
            />
            <Tooltip 
              content={<CustomTooltip />}
              allowEscapeViewBox={{ x: false, y: false }}
            />
            
            {/* Current Pay Structure Bar */}
            <Bar
              dataKey="oldPay"
              fill="var(--color-oldPay)"
              radius={[2, 2, 0, 0]}
              opacity={0.8}
            />
            
            {/* New Pay Structure Bar */}
            <Bar
              dataKey="newPay"
              fill="var(--color-newPay)"
              radius={[2, 2, 0, 0]}
              opacity={0.9}
            />
            <ChartLegend content={<ChartLegendContent />} className="mt-8" />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
} 